import { ApolloClient, InMemoryCache, HttpLink, ApolloLink } from "@apollo/client";
import { ErrorLink } from "@apollo/client/link/error";
import { CombinedGraphQLErrors, CombinedProtocolErrors } from "@apollo/client/errors";
import { SetContextLink } from "@apollo/client/link/context";
import { graphQlInterceptorLink } from "./graphQl.interceptorLink";
import { graphQlVariablesLink } from "./graphQlVariablesLink";
import { createLogger, type ILogger } from "@/infrastructure/services/logger";
import { AppConfig } from "@src/config/AppConfig";
import { AuthUserTokenService } from "@src/infrastructure/auth/AuthUserTokenService";

const GRAPHQL_BASE_URL = AppConfig.getGraphQLUrl();

/**
 * Dedicated Apollo client for Config tenant queries.
 * Sends `X-Brand-Key` so the admin API can resolve the brand tenant DB.
 * Do not use for Master Data or other non–Config admin GraphQL.
 */
export class ConfigGraphQlAdapter {
  private readonly client: ApolloClient;
  private static instance: ConfigGraphQlAdapter;
  private readonly logger: ILogger = createLogger("ConfigGraphQlAdapter");
  private brandKey: string | null = null;

  private constructor() {
    this.client = this.initializeClient();
  }

  public static getInstance(): ConfigGraphQlAdapter {
    if (!ConfigGraphQlAdapter.instance) {
      ConfigGraphQlAdapter.instance = new ConfigGraphQlAdapter();
    }
    return ConfigGraphQlAdapter.instance;
  }

  public setBrandKey(brandKey: string | null): void {
    this.brandKey = brandKey?.trim() || null;
  }

  public getBrandKey(): string | null {
    return this.brandKey;
  }

  protected async getUserAccessToken() {
    return new AuthUserTokenService().getAuthAccessToken();
  }

  protected async getAdminAccessToken() {
    return new AuthUserTokenService().getAdminAccessToken();
  }

  protected async getAdminRefreshToken() {
    return new AuthUserTokenService().getAdminRefreshToken();
  }

  private async getAuthTokens() {
    const userAccessToken = await this.getUserAccessToken();
    const adminAccessToken = await this.getAdminAccessToken();
    const adminRefreshToken = await this.getAdminRefreshToken();
    return { userAccessToken, adminAccessToken, adminRefreshToken };
  }

  private initializeClient(): ApolloClient {
    const httpLink = new HttpLink({
      uri: GRAPHQL_BASE_URL,
    });

    const errorLink = new ErrorLink(({ error }) => {
      if (CombinedGraphQLErrors.is(error)) {
        for (const { message, locations, path } of error.errors) {
          this.logger.error(
            `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${JSON.stringify(path)}`,
            error,
          );
        }
      } else if (CombinedProtocolErrors.is(error)) {
        for (const { message, extensions } of error.errors) {
          this.logger.error(
            `[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(extensions)}`,
            error,
          );
        }
      } else {
        this.logger.error(`[Network error]: ${error.message}`, error);
      }
    });

    const authLink = new SetContextLink(async (prevContext) => {
      const tokens = await this.getAuthTokens();
      const headers: Record<string, string> = {
        ...(prevContext.headers as Record<string, string> | undefined),
        "Content-Type": "application/json",
        Accept: "application/json",
      };

      if (tokens.userAccessToken) {
        headers.Authorization = `Bearer ${tokens.userAccessToken}`;
      }
      if (tokens.adminAccessToken) {
        headers["X-Admin-Authorization"] = `Bearer ${tokens.adminAccessToken}`;
      }
      if (tokens.adminRefreshToken) {
        headers["X-Admin-Refresh-Token"] = `Bearer ${tokens.adminRefreshToken}`;
      }
      const requestBrandKey =
        typeof prevContext.brandKey === "string" && prevContext.brandKey.trim()
          ? prevContext.brandKey.trim()
          : this.brandKey;
      if (requestBrandKey) {
        headers["X-Brand-Key"] = requestBrandKey;
      }

      return {
        ...prevContext,
        headers,
      };
    });

    return new ApolloClient({
      link: ApolloLink.from([
        errorLink,
        authLink,
        graphQlInterceptorLink,
        graphQlVariablesLink,
        httpLink,
      ]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          errorPolicy: "all",
        },
        query: {
          errorPolicy: "all",
        },
        mutate: {
          errorPolicy: "all",
        },
      },
    });
  }

  getClient(): ApolloClient {
    return this.client;
  }
}
